import { useCallback, useEffect, useId, useState } from "react";
import { Lock } from "lucide-react";

import { businessApi } from "../../../../services/api";
import { Button, Status, Surface } from "../../../../components/ui";
import {
  basisMethodLabel, FACT_LABELS, factPeriodText, factStateLabel, factValueText, formatLocalDate, groupFacts,
  PHILHEALTH_BASIS_METHODS,
} from "../statutoryView";

// Statutory payroll facts (CD-S1): things HR records about an employee so that government contributions and
// withholding tax can later be worked out. They are not pay and never change the employee's compensation. A record is
// never edited: a new one takes over from its start date, and the earlier one stays in the history.

const inputCls = "w-full rounded-control border border-default bg-surface px-3 py-2 text-body text-foreground hover:border-strong "
  + "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus";
const labelCls = "block text-label text-secondary";

function FactSummary({ fact, muted = false }) {
  return (
    <div className={muted ? "text-secondary" : ""}>
      <p className={`text-body font-semibold ${muted ? "" : "text-foreground"}`}>
        {factValueText(fact)}
        {fact.state !== "current" ? (
          <Status className="ml-2 font-normal">{factStateLabel(fact.state)}</Status>
        ) : null}
      </p>
      <p className="mt-0.5 text-meta text-secondary">
        {factPeriodText(fact)}
        {fact.basisMethod ? `. ${basisMethodLabel(fact.basisMethod)}` : ""}
      </p>
      <p className="mt-1 text-body-sm text-foreground">Reason: {fact.reason}</p>
      <p className="mt-0.5 text-meta text-tertiary">
        Recorded{fact.recordedByName ? ` by ${fact.recordedByName}` : ""}{fact.recordedAt ? ` on ${formatLocalDate(fact.recordedAt)}` : ""}
        {fact.cancelledAt && fact.cancelReason ? `. Cancelled: ${fact.cancelReason}` : ""}
      </p>
    </div>
  );
}

function RecordForm({ factType, today, onCancel, onSubmit, saving, error }) {
  const ids = { value: useId(), amount: useId(), method: useId(), date: useId(), year: useId(), reason: useId(), error: useId() };
  const [form, setForm] = useState({
    value: "", amount: "", basisMethod: "HR_DECLARED", effectiveFrom: today || "", calendarYear: String(today || "").slice(0, 4), reason: "",
  });
  const [problem, setProblem] = useState("");
  const set = (key) => (event) => { setForm((current) => ({ ...current, [key]: event.target.value })); setProblem(""); };
  const isBasis = factType === "PHILHEALTH_MBS";
  const isYear = factType === "PRIOR_EMPLOYER_IN_YEAR";

  const submit = (event) => {
    event.preventDefault();
    if (saving) return;
    const payload = { factType, reason: form.reason.trim() };
    if (isBasis) {
      const pesos = Number(form.amount);
      if (!form.amount || !Number.isFinite(pesos) || pesos <= 0) return setProblem("Enter the monthly basic salary basis as an amount above zero.");
      if (pesos > 10_000_000) return setProblem("That amount is too large. Check the figure.");
      payload.amountCents = Math.round(pesos * 100);
      payload.basisMethod = form.basisMethod;
    } else {
      if (form.value !== "yes" && form.value !== "no") return setProblem("Choose yes or no.");
      payload.value = form.value === "yes";
    }
    if (isYear) {
      const year = Number(form.calendarYear);
      if (!Number.isInteger(year) || year < 2000 || year > 2100) return setProblem("Enter the calendar year, for example 2026.");
      payload.calendarYear = year;
    } else {
      if (!form.effectiveFrom) return setProblem("Choose the date this starts.");
      payload.effectiveFrom = form.effectiveFrom;
    }
    if (payload.reason.length < 3) return setProblem("Give the reason or the basis for this record.");
    return onSubmit(payload);
  };

  const shown = problem || error;
  const question = isYear ? "Did the employee have another employer in this year?" : "Is the employee a minimum wage earner?";

  return (
    <form onSubmit={submit} noValidate aria-describedby={shown ? ids.error : undefined}
      className="mt-3 space-y-3 rounded-panel border border-subtle bg-surface-sunken p-3">
      {isBasis ? (
        <>
          <p className="rounded-control border border-warning bg-warning-subtle px-3 py-2 text-body-sm text-foreground">
            Used only for PhilHealth contribution calculation. This does not change the employee's pay.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor={ids.amount} className={labelCls}>Monthly basic salary basis (₱)</label>
              <input id={ids.amount} type="number" inputMode="decimal" min="0.01" step="0.01" value={form.amount} onChange={set("amount")} data-ui="input" className={`mt-1.5 ${inputCls}`} />
            </div>
            <div>
              <label htmlFor={ids.method} className={labelCls}>How it was worked out</label>
              <select id={ids.method} value={form.basisMethod} onChange={set("basisMethod")} data-ui="input" className={`mt-1.5 ${inputCls}`}>
                {PHILHEALTH_BASIS_METHODS.map((method) => <option key={method.value} value={method.value}>{method.label}</option>)}
              </select>
            </div>
          </div>
        </>
      ) : (
        <fieldset>
          <legend className={labelCls}>{question}</legend>
          <div className="mt-1.5 flex gap-4">
            {[["yes", "Yes"], ["no", "No"]].map(([value, label]) => (
              <label key={value} className="inline-flex min-h-touch items-center gap-2 text-body text-foreground md:min-h-0">
                <input type="radio" name={ids.value} value={value} checked={form.value === value} onChange={set("value")} className="h-4 w-4" />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {isYear ? (
          <div>
            <label htmlFor={ids.year} className={labelCls}>Calendar year</label>
            <input id={ids.year} type="number" inputMode="numeric" min="2000" max="2100" step="1" value={form.calendarYear} onChange={set("calendarYear")} data-ui="input" className={`mt-1.5 ${inputCls}`} />
          </div>
        ) : (
          <div>
            <label htmlFor={ids.date} className={labelCls}>Starts on</label>
            <input id={ids.date} type="date" value={form.effectiveFrom} onChange={set("effectiveFrom")} data-ui="input" className={`mt-1.5 ${inputCls}`} />
          </div>
        )}
        <div className={isYear ? "" : "sm:col-span-1"}>
          <label htmlFor={ids.reason} className={labelCls}>Reason or basis</label>
          <input id={ids.reason} type="text" maxLength={500} value={form.reason} onChange={set("reason")} data-ui="input" className={`mt-1.5 ${inputCls}`}
            placeholder={isBasis ? "For example: daily rate 650 × 313 ÷ 12" : "For example: the wage order or document relied on"} />
        </div>
      </div>

      {shown ? <p id={ids.error} role="alert" className="text-body-sm text-danger">{shown}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" variant="primary" loading={saving}>{saving ? "Saving…" : "Save record"}</Button>
        <Button size="sm" variant="quiet" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </form>
  );
}

function CancelForm({ onClose, onSubmit, saving, error }) {
  const id = useId();
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState("");
  const submit = (event) => {
    event.preventDefault();
    if (saving) return;
    if (reason.trim().length < 3) { setProblem("Say why this record is being cancelled."); return; }
    onSubmit(reason.trim());
  };
  return (
    <form onSubmit={submit} noValidate className="mt-3 rounded-panel border border-subtle bg-surface-sunken p-3">
      <label htmlFor={id} className={labelCls}>Why is this record being cancelled?</label>
      <input id={id} type="text" maxLength={500} value={reason} autoFocus onChange={(event) => { setReason(event.target.value); setProblem(""); }} data-ui="input" className={`mt-1.5 ${inputCls}`} />
      <p className="mt-1.5 text-meta text-tertiary">The record stays in the history, marked as cancelled.</p>
      {problem || error ? <p role="alert" className="mt-2 text-body-sm text-danger">{problem || error}</p> : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="submit" size="sm" variant="danger" loading={saving}>{saving ? "Cancelling…" : "Cancel this record"}</Button>
        <Button size="sm" variant="quiet" onClick={onClose} disabled={saving}>Keep it</Button>
      </div>
    </form>
  );
}

export default function StatutoryFactsPanel({ memberId, reloadKey = 0 }) {
  const [state, setState] = useState({ loading: true, error: "", restricted: false, data: null });
  const [open, setOpen] = useState(null); // { factType, mode: "record" | "cancel" }
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await businessApi.getEmployeeStatutoryFacts(memberId);
      setState({ loading: false, error: "", restricted: false, data });
    } catch (err) {
      if (err?.status === 403) setState({ loading: false, error: "", restricted: true, data: null });
      else setState({ loading: false, error: err?.serverMessage || "The payroll facts could not be loaded.", restricted: false, data: null });
    }
  }, [memberId]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const run = async (work, done) => {
    setSaving(true);
    setFormError("");
    try {
      const data = await work();
      setState({ loading: false, error: "", restricted: false, data });
      setOpen(null);
      setMessage(done);
    } catch (err) {
      setFormError(err?.serverMessage || "This could not be saved. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const { loading, error, restricted, data } = state;
  const canManage = Boolean(data?.canManage);
  const groups = groupFacts(data?.facts || []);

  return (
    <Surface
      as="section"
      aria-label="Statutory payroll facts"
      toolbar={(
        <div>
          <h2 className="text-panel-title text-foreground">Statutory payroll facts</h2>
          <p className="text-body-sm text-secondary">Facts HR records so government contributions and tax can be worked out. They never change the employee's pay.</p>
        </div>
      )}
    >
      {loading ? (
        <p className="p-4 text-body-sm text-tertiary">Loading…</p>
      ) : restricted ? (
        <div className="flex items-start gap-3 p-4 text-body-sm text-secondary">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-tertiary" aria-hidden="true" />
          <p>Statutory payroll facts are part of employee pay information, which is not part of your role.</p>
        </div>
      ) : error ? (
        <div className="p-4">
          <p role="alert" className="text-body-sm text-danger">{error}</p>
          <Button size="sm" className="mt-3" onClick={load}>Try again</Button>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-subtle">
            {groups.map(({ factType, current, upcoming, history, cancellableId }) => {
              const text = FACT_LABELS[factType];
              const recording = open?.factType === factType && open.mode === "record";
              const cancelling = open?.factType === factType && open.mode === "cancel";
              const newest = [current, ...upcoming].filter(Boolean).find((fact) => fact.id === cancellableId) || null;
              return (
                <li key={factType} className="px-4 py-4" aria-label={text.title}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h3 className="text-panel-title text-foreground">{text.title}</h3>
                      <p className="mt-0.5 max-w-prose text-meta text-tertiary">{text.help}</p>
                    </div>
                    {canManage && !recording && !cancelling ? (
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <Button size="sm" onClick={() => { setFormError(""); setOpen({ factType, mode: "record" }); }}>
                          {current || upcoming.length ? "Record a change" : text.action}
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-3 space-y-3">
                    {current ? <FactSummary fact={current} /> : (
                      <p className="text-body-sm text-secondary">Not recorded yet.</p>
                    )}
                    {upcoming.map((fact) => <FactSummary key={fact.id} fact={fact} />)}
                  </div>

                  {canManage && newest && !recording && !cancelling ? (
                    <Button size="sm" variant="quiet" className="mt-2"
                      onClick={() => { setFormError(""); setOpen({ factType, mode: "cancel", factId: newest.id }); }}>
                      Entered by mistake? Cancel the latest record
                    </Button>
                  ) : null}

                  {recording ? (
                    <RecordForm factType={factType} today={data?.today} saving={saving} error={formError}
                      onCancel={() => setOpen(null)}
                      onSubmit={(payload) => run(() => businessApi.recordEmployeeStatutoryFact(memberId, payload), `${text.title} saved.`)} />
                  ) : null}
                  {cancelling ? (
                    <CancelForm saving={saving} error={formError} onClose={() => setOpen(null)}
                      onSubmit={(reason) => run(() => businessApi.cancelEmployeeStatutoryFact(memberId, open.factId, reason), `${text.title} record cancelled.`)} />
                  ) : null}

                  {history.length ? (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-body-sm font-medium text-secondary hover:text-foreground">
                        History ({history.length})
                      </summary>
                      <ul className="mt-2 space-y-3 border-l border-subtle pl-3">
                        {history.map((fact) => <li key={fact.id}><FactSummary fact={fact} muted /></li>)}
                      </ul>
                    </details>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <p role="status" aria-live="polite"
            className={message ? "border-t border-subtle px-4 py-2.5 text-body-sm text-secondary" : "sr-only"}>
            {message}
          </p>
        </>
      )}
    </Surface>
  );
}
